import * as React from "react";
import { Popover, PopoverContent, PopoverAnchor } from "./popover";
import { Input, type InputProps } from "./input";
import { BaseInputField, type BaseInputFieldProps } from "./base-input-field";
import { cn } from "../../lib/utils";

export type AutocompleteInputType = "input" | "base-input-field";

export interface AutocompleteOption<T = unknown> {
  value: string;
  label: string;
  data?: T;
}

export interface AutocompleteRenderInputParams {
  id: string;
  value: string;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onFocus: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  autoComplete: string;
  ref: React.Ref<any>;
}

type BaseAutocompleteProps<T = unknown> = {
  options:
    | AutocompleteOption<T>[]
    | ((searchValue: string) => AutocompleteOption<T>[]);
  value?: string;
  defaultValue?: string;
  onChange?: (value: string, option?: AutocompleteOption<T>) => void;
  onSelect?: (option: AutocompleteOption<T>) => void;
  className?: string;
  inputClassName?: string;
  dropdownClassName?: string;
  emptyMessage?: string;
  renderOption?: (
    option: AutocompleteOption<T>,
    index: number
  ) => React.ReactNode;
  renderEmpty?: () => React.ReactNode;
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
};

type AutocompleteWithInput<T = unknown> = BaseAutocompleteProps<T> & {
  inputType?: "input";
  inputProps?: Omit<
    InputProps,
    "value" | "defaultValue" | "onChange" | "autoComplete"
  >;
  renderInput?: never;
};

type AutocompleteWithBaseInputField<T = unknown> = BaseAutocompleteProps<T> & {
  inputType: "base-input-field";
  inputProps?: Omit<
    BaseInputFieldProps,
    "value" | "defaultValue" | "onChange" | "autoComplete"
  >;
  renderInput?: never;
};

type AutocompleteWithRenderInput<T = unknown> = BaseAutocompleteProps<T> & {
  renderInput: (params: AutocompleteRenderInputParams) => React.ReactNode;
  inputType?: never;
  inputProps?: never;
};

export type AutocompleteProps<T = unknown> =
  | AutocompleteWithInput<T>
  | AutocompleteWithBaseInputField<T>
  | AutocompleteWithRenderInput<T>;

const Autocomplete = <T,>({
  inputType = "input",
  options,
  value,
  defaultValue,
  onChange,
  onSelect,
  className,
  inputClassName,
  dropdownClassName,
  emptyMessage = "No options found",
  renderOption,
  renderEmpty,
  renderInput,
  inputProps,
  ...rest
}: AutocompleteProps<T>) => {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState(
    value ?? defaultValue ?? ""
  );
  const [selectedOption, setSelectedOption] = React.useState<
    AutocompleteOption<T> | undefined
  >();
  const inputRef = React.useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const defaultInputRef = React.useRef<HTMLInputElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Get filtered options
  const getFilteredOptions = (): AutocompleteOption<T>[] => {
    if (typeof options === "function") {
      return options(searchValue);
    }

    // Default filtering: case-insensitive search
    if (!searchValue.trim()) {
      return options;
    }

    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(searchValue.toLowerCase()) ||
        option.value.toLowerCase().includes(searchValue.toLowerCase())
    );
  };

  const filteredOptions = getFilteredOptions();

  // Handle input change
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newValue = e.target.value;
    setSearchValue(newValue);
    setOpen(true);
    onChange?.(newValue);
  };

  // Handle option selection
  const handleSelectOption = (option: AutocompleteOption<T>) => {
    setSearchValue(option.label);
    setSelectedOption(option);
    setOpen(false);
    onChange?.(option.value, option);
    onSelect?.(option);
    if (renderInput) {
      inputRef.current?.blur();
    } else {
      defaultInputRef.current?.blur();
    }
  };

  // Handle input focus
  const handleFocus = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setOpen(true);
    if (!renderInput && inputType === "base-input-field") {
      (inputProps as BaseInputFieldProps)?.onFocus?.(
        e as React.FocusEvent<HTMLInputElement>
      );
    } else if (!renderInput) {
      (inputProps as InputProps)?.onFocus?.(
        e as React.FocusEvent<HTMLInputElement>
      );
    }
  };

  // Handle input blur
  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    // Delay closing to allow option click
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setOpen(false);
      }
    }, 200);
    if (!renderInput && inputType === "base-input-field") {
      (inputProps as BaseInputFieldProps)?.onBlur?.(
        e as React.FocusEvent<HTMLInputElement>
      );
    } else if (!renderInput) {
      (inputProps as InputProps)?.onBlur?.(
        e as React.FocusEvent<HTMLInputElement>
      );
    }
  };

  // Sync value prop
  React.useEffect(() => {
    if (value !== undefined && value !== null) {
      setSearchValue(value);
    }
  }, [value]);

  // Find option by value
  React.useEffect(() => {
    if (value !== undefined && typeof options !== "function") {
      const option = options.find((opt) => opt.value === value);
      if (option) {
        setSelectedOption(option);
        setSearchValue(option.label);
      }
    }
  }, [value, options]);

  const defaultRenderOption = (
    option: AutocompleteOption<T>,
    index: number
  ) => (
    <div
      key={`${option.value}-${index}`}
      className={cn(
        "ui-relative ui-flex ui-cursor-pointer ui-select-none ui-items-center ui-rounded-sm ui-px-2 ui-py-1.5 ui-text-sm ui-outline-none",
        "ui-transition-colors",
        "hover:ui-bg-accent hover:ui-text-accent-foreground",
        "focus:ui-bg-accent focus:ui-text-accent-foreground",
        "data-[highlighted]:ui-bg-accent data-[highlighted]:ui-text-accent-foreground",
        "ui-touch-manipulation"
      )}
      onClick={() => handleSelectOption(option)}
    >
      {option.label}
    </div>
  );

  const defaultRenderEmpty = () => (
    <div className="ui-text-sm ui-text-muted-foreground">{emptyMessage}</div>
  );

  const inputId = inputProps?.id || `autocomplete-${React.useId()}`;

  const handleOpenChange = (newOpen: boolean) => {
    // Don't allow Popover to force close if we're currently focused
    if (!newOpen) {
      const isFocused =
        document.activeElement === inputRef.current ||
        document.activeElement === defaultInputRef.current;

      if (isFocused) {
        // Input is focused, don't close
        return;
      }
    }
    setOpen(newOpen);
  };

  const shouldShowDropdown =
    open && (filteredOptions.length > 0 || renderEmpty !== undefined);

  return (
    <div ref={containerRef} className={cn("ui-relative ui-w-full", className)}>
      <Popover open={shouldShowDropdown} onOpenChange={handleOpenChange}>
        <PopoverAnchor asChild>
          <div className="ui-w-full">
            {renderInput ? (
              renderInput({
                id: inputId,
                value: searchValue,
                onChange: handleInputChange,
                onFocus: handleFocus,
                onBlur: handleBlur,
                autoComplete: "off",
                ref: inputRef,
              })
            ) : inputType === "base-input-field" ? (
              <BaseInputField
                ref={defaultInputRef}
                id={inputId}
                value={searchValue}
                defaultValue={defaultValue}
                onChange={handleInputChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                autoComplete="off"
                className={inputClassName}
                {...(inputProps as Omit<
                  BaseInputFieldProps,
                  "value" | "defaultValue" | "onChange" | "autoComplete"
                >)}
                {...rest}
              />
            ) : (
              <Input
                ref={defaultInputRef}
                id={inputId}
                value={searchValue}
                defaultValue={defaultValue}
                onChange={handleInputChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                autoComplete="off"
                className={inputClassName}
                {...(inputProps as Omit<
                  InputProps,
                  "value" | "defaultValue" | "onChange" | "autoComplete"
                >)}
                {...rest}
              />
            )}
          </div>
        </PopoverAnchor>
        <PopoverContent
          className={cn(
            "ui-w-[var(--radix-popover-trigger-width)] !ui-p-0 ui-overflow-hidden",
            dropdownClassName
          )}
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {filteredOptions.length > 0 ? (
            <div
              className="ui-max-h-[230px] ui-overflow-y-scroll ui-p-1 ui-overscroll-contain"
              style={
                {
                  WebkitOverflowScrolling: "touch",
                  touchAction: "pan-y",
                  overscrollBehavior: "contain",
                  pointerEvents: "auto",
                  scrollbarWidth: "thin",
                  msOverflowStyle: "auto",
                } as React.CSSProperties
              }
              onTouchStart={(e) => {
                // Prevent event from being captured by parent
                e.stopPropagation();
              }}
              onTouchMove={(e) => {
                // Allow scrolling by stopping propagation
                e.stopPropagation();
              }}
              onWheel={(e) => {
                // Allow mouse wheel scrolling by stopping propagation
                e.stopPropagation();
              }}
            >
              {filteredOptions.map((option, index) =>
                renderOption
                  ? renderOption(option, index)
                  : defaultRenderOption(option, index)
              )}
            </div>
          ) : renderEmpty ? (
            <div className="ui-p-2">{renderEmpty()}</div>
          ) : (
            <div className="ui-p-2">{defaultRenderEmpty()}</div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

Autocomplete.displayName = "Autocomplete";

export { Autocomplete };
